/* eslint-disable */
// @ts-ignore
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

interface CellRange {
  s: { r: number; c: number };
  e: { r: number; c: number };
}

interface CellValue {
  v: any;
  t?: string;
  z?: string;
}

interface Worksheet {
  [key: string]: CellValue | string | CellRange[] | Array<{ wch: number }> | undefined;
}

interface Workbook {
  SheetNames: string[];
  Sheets: { [key: string]: Worksheet };
}

interface ExportOptions {
  multiHeader?: string[][];
  header: string[];
  data: any[][];
  filename?: string;
  merges?: string[];
  autoWidth?: boolean;
  bookType?: 'xlsx' | 'xls' | 'csv';
}

interface MultiSheetData {
  sheetTitle: string;
  data: any[][];
}

interface MultiSheetExportOptions {
  multiHeader?: string[][];
  header: string[][];
  data: MultiSheetData[];
  filename?: string;
  merges?: string[];
  autoWidth?: boolean;
  bookType?: 'xlsx' | 'xls' | 'csv';
}

function generateArray(table: HTMLTableElement): [any[][], CellRange[]] {
  const out: any[][] = [];
  const rows = table.querySelectorAll('tr');
  const ranges: CellRange[] = [];
  
  for (let R = 0; R < rows.length; ++R) {
    const outRow: any[] = [];
    const row = rows[R];
    const columns = row.querySelectorAll('td');
    
    for (let C = 0; C < columns.length; ++C) {
      const cell = columns[C] as HTMLTableCellElement;
      const colspan = cell.getAttribute('colspan');
      const rowspan = cell.getAttribute('rowspan');
      let cellValue: any = cell.innerText;
      
      if (cellValue !== "" && cellValue == +cellValue) {
        cellValue = +cellValue;
      }

      // Skip ranges
      ranges.forEach((range) => {
        if (R >= range.s.r && R <= range.e.r && outRow.length >= range.s.c && outRow.length <= range.e.c) {
          for (let i = 0; i <= range.e.c - range.s.c; ++i) {
            outRow.push(null);
          }
        }
      });

      // Handle Row Span
      if (rowspan || colspan) {
        const rowspanNum = parseInt(rowspan || '1');
        const colspanNum = parseInt(colspan || '1');
        ranges.push({
          s: {
            r: R,
            c: outRow.length
          },
          e: {
            r: R + rowspanNum - 1,
            c: outRow.length + colspanNum - 1
          }
        });
      }

      // Handle Value
      outRow.push(cellValue !== "" ? cellValue : null);

      // Handle Colspan
      if (colspan) {
        const colspanNum = parseInt(colspan);
        for (let k = 0; k < colspanNum - 1; ++k) {
          outRow.push(null);
        }
      }
    }
    out.push(outRow);
  }
  return [out, ranges];
}

function datenum(v: string | Date, date1904?: boolean): number {
  let date: Date;
  if (typeof v === 'string') {
    date = new Date(v);
  } else {
    date = v;
  }
  
  if (date1904) {
    date = new Date(date.getTime() + 1462 * 24 * 60 * 60 * 1000);
  }
  
  const epoch = Date.parse(date.toString());
  return (epoch - new Date(Date.UTC(1899, 11, 30)).getTime()) / (24 * 60 * 60 * 1000);
}

function sheet_from_array_of_arrays(data: any[][], opts?: any): Worksheet {
  const ws: Worksheet = {};
  const range = {
    s: { c: 10000000, r: 10000000 },
    e: { c: 0, r: 0 }
  };
  
  for (let R = 0; R !== data.length; ++R) {
    for (let C = 0; C !== data[R].length; ++C) {
      if (range.s.r > R) range.s.r = R;
      if (range.s.c > C) range.s.c = C;
      if (range.e.r < R) range.e.r = R;
      if (range.e.c < C) range.e.c = C;
      
      const cell: CellValue = {
        v: data[R][C]
      };
      
      if (cell.v == null) continue;
      
      const cell_ref = XLSX.utils.encode_cell({
        c: C,
        r: R
      });

      if (typeof cell.v === 'number') {
        cell.t = 'n';
      } else if (typeof cell.v === 'boolean') {
        cell.t = 'b';
      } else if (cell.v instanceof Date) {
        cell.t = 'n';
        cell.z = XLSX.SSF._table[14];
        cell.v = datenum(cell.v);
      } else {
        cell.t = 's';
      }

      ws[cell_ref] = cell;
    }
  }
  
  if (range.s.c < 10000000) {
    ws['!ref'] = XLSX.utils.encode_range(range);
  }
  
  return ws;
}

class Workbook {
  SheetNames: string[] = [];
  Sheets: { [key: string]: Worksheet } = {};
  
  constructor() {
    this.SheetNames = [];
    this.Sheets = {};
  }
}

function s2ab(s: string): ArrayBuffer {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i !== s.length; ++i) {
    view[i] = s.charCodeAt(i) & 0xFF;
  }
  return buf;
}

export function export_table_to_excel(id: string): void {
  const theTable = document.getElementById(id) as HTMLTableElement;
  if (!theTable) {
    console.error(`Table with id "${id}" not found`);
    return;
  }
  
  const oo = generateArray(theTable);
  const ranges = oo[1];
  const data = oo[0];
  const ws_name = "SheetJS";

  const wb = new Workbook();
  const ws = sheet_from_array_of_arrays(data);

  // Add ranges to worksheet
  ws['!merges'] = ranges;

  // Add worksheet to workbook
  wb.SheetNames.push(ws_name);
  wb.Sheets[ws_name] = ws;

  const wbout = XLSX.write(wb, {
    bookType: 'xlsx',
    bookSST: false,
    type: 'binary'
  });

  saveAs(new Blob([s2ab(wbout)], {
    type: "application/octet-stream"
  }), "test.xlsx");
}

export function export_json_to_excel({
  multiHeader = [],
  header,
  data,
  filename = 'excel-list',
  merges = [],
  autoWidth = true,
  bookType = 'xlsx'
}: ExportOptions): void {
  const dataCopy = [...data];
  dataCopy.unshift(header);

  for (let i = multiHeader.length - 1; i > -1; i--) {
    dataCopy.unshift(multiHeader[i]);
  }

  const ws_name = "SheetJS";
  const wb = new Workbook();
  const ws = sheet_from_array_of_arrays(dataCopy);

  if (merges.length > 0) {
    if (!ws['!merges']) ws['!merges'] = [];
    merges.forEach(item => {
      (ws['!merges'] as CellRange[]).push(XLSX.utils.decode_range(item));
    });
  }

  if (autoWidth) {
    // 设置worksheet每列的最大宽度
    const colWidth = dataCopy.map(row => row.map(val => {
      // 先判断是否为null/undefined
      if (val == null) {
        return { 'wch': 10 };
      }
      // 再判断是否为中文
      else if (val.toString().charCodeAt(0) > 255) {
        return { 'wch': val.toString().length * 2 };
      } else {
        return { 'wch': val.toString().length };
      }
    }));
    
    // 以第一行为初始值
    let result = colWidth[0];
    for (let i = 1; i < colWidth.length; i++) {
      for (let j = 0; j < colWidth[i].length; j++) {
        if (result[j]['wch'] < colWidth[i][j]['wch']) {
          result[j]['wch'] = colWidth[i][j]['wch'];
        }
      }
    }
    ws['!cols'] = result;
  }

  // Add worksheet to workbook
  wb.SheetNames.push(ws_name);
  wb.Sheets[ws_name] = ws;

  const wbout = XLSX.write(wb, {
    bookType: bookType,
    bookSST: false,
    type: 'binary'
  });
  
  saveAs(new Blob([s2ab(wbout)], {
    type: "application/octet-stream"
  }), `${filename}.${bookType}`);
}

export function export_json_to_excel_multi_sheet({
  multiHeader = [],
  header,
  data,
  filename = 'excel-list',
  merges = [],
  autoWidth = true,
  bookType = 'xlsx'
}: MultiSheetExportOptions): void {
  const wb = new Workbook();

  // 生成多个sheet
  for (let y = 0; y < data.length; y++) {
    const item = data[y];
    const itemData = [...item.data];
    itemData.unshift(header[y]);
    const ws_name = item.sheetTitle;

    const ws = sheet_from_array_of_arrays(itemData);

    if (merges.length > 0) {
      if (!ws['!merges']) ws['!merges'] = [];
      merges.forEach(mergeItem => {
        (ws['!merges'] as CellRange[]).push(XLSX.utils.decode_range(mergeItem));
      });
    }

    if (autoWidth) {
      // 设置worksheet每列的最大宽度
      const colWidth = itemData.map(row => row.map(val => {
        // 先判断是否为null/undefined
        if (val == null) {
          return { 'wch': 10 };
        }
        // 再判断是否为中文
        else if (val.toString().charCodeAt(0) > 255) {
          return { 'wch': val.toString().length * 2 };
        } else {
          return { 'wch': val.toString().length };
        }
      }));
      
      // 以第一行为初始值
      let result = colWidth[0];
      for (let i = 1; i < colWidth.length; i++) {
        for (let j = 0; j < colWidth[i].length; j++) {
          if (result[j]['wch'] < colWidth[i][j]['wch']) {
            result[j]['wch'] = colWidth[i][j]['wch'];
          }
        }
      }
      ws['!cols'] = result;
    }

    // Add worksheet to workbook
    wb.SheetNames.push(ws_name);
    wb.Sheets[ws_name] = ws;
  }

  const wbout = XLSX.write(wb, {
    bookType: bookType,
    bookSST: false,
    type: 'binary'
  });
  
  saveAs(new Blob([s2ab(wbout)], {
    type: "application/octet-stream"
  }), `${filename}.${bookType}`);
}
